import './style.scss';

import { DatePicker, DatePickerProps, FormInstance, Select, Space } from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { DefaultOptionType, LabelInValueType } from 'rc-select/lib/Select';
import React, { useEffect } from 'react';

import { useGetDictionariesQuery, useGetDictionaryQuery } from '@/api/dictionaries/api';
import { IndividualEvent } from '@/api/vaccination/registry/types';
import { EventProtocol } from '@/pages/Vaccination/components/protocols/types';
import { DateTimeFormat } from '@/utils/dateUtils';

export interface SelectProtocolsProps {
  value?: IndividualEvent[];
  onChange: (value: IndividualEvent[] | undefined, option?: DefaultOptionType) => void;
  form: FormInstance;
  disabled?: boolean;
  dataRequest: {
    dictionaryName: string;
    filters: {
      organizationId: string;
      endAgeFrom: number;
    };
  };
}

export const SelectProtocols: React.FC<SelectProtocolsProps> = ({
  value,
  onChange,
  form,
  disabled,
  dataRequest,
}) => {
  const { data: dictionaries } = useGetDictionariesQuery();
  const { data: events } = useGetDictionaryQuery(dataRequest, { skip: !dataRequest });

  const today = dayjs().startOf('day');
  const birthDateString = form.getFieldValue('birthDate');
  const birthDate =
    birthDateString && dayjs(birthDateString, DateTimeFormat.DATE).startOf('day');

  const getValidMinDate = (ageFrom?: number | null) => {
    const minDate = ageFrom ? birthDate.add(ageFrom, 'day') : birthDate;
    return (minDate.isBefore(today) ? minDate : today).startOf('day');
  };
  const getValidMaxDate = (ageTo?: number | null) => {
    const maxDate = ageTo ? birthDate.add(ageTo, 'day') : today;
    return (maxDate.isBefore(today) ? maxDate : today).endOf('day');
  };

  useEffect(() => {
    if (!birthDate) {
      onChange([]);
      return;
    }

    const today = dayjs().startOf('day');
    const age = today.diff(birthDate, 'day');

    if (_.isEmpty(events)) {
      onChange([]);
      return;
    }

    const selected = (value || []).filter((event: IndividualEvent) =>
      events.includes(event.eventProtocolId),
    );

    const additionalEvents = events
      .filter(
        (event: EventProtocol) =>
          (!event.ageFrom || event.ageFrom <= age) &&
          !selected.some((selectedEvent) => selectedEvent.eventProtocolId === event.id),
      )
      .map((event: EventProtocol) => ({
        eventProtocolId: event.id,
        dateAt: getValidMinDate(event?.ageFrom).format(DateTimeFormat.LOCAL_DATE),
      }));

    onChange([...selected, ...additionalEvents]);
  }, [events, birthDateString]);

  const datesMap = value
    ? Object.fromEntries(
        value
          .filter(({ dateAt }) => !!dateAt)
          .map(({ eventProtocolId, dateAt }) => [
            eventProtocolId,
            dayjs(dateAt, DateTimeFormat.LOCAL_DATE),
          ]),
      )
    : {};
  const mapValue = (values?: IndividualEvent[]) => {
    return values?.map((value) => value.eventProtocolId);
  };
  const handleDatePickerChange = (selected: string, date?: Dayjs) => {
    const defaultDate = getValidMinDate(
      events.find((event: EventProtocol) => event.id === selected)?.ageFrom,
    );
    onChange(
      (value ?? []).map((item) =>
        item.eventProtocolId === selected
          ? {
              eventProtocolId: selected,
              dateAt: date?.format(DateTimeFormat.LOCAL_DATE) ?? defaultDate,
            }
          : item,
      ),
    );
  };
  const handleChange = (selected: string[]) => {
    const mapped = events
      .filter((event: EventProtocol) => selected.includes(event.id))
      .map((event: EventProtocol) => ({
        eventProtocolId: event.id,
        dateAt: getValidMinDate(event?.ageFrom).format(DateTimeFormat.LOCAL_DATE),
      }));
    onChange(mapped);
  };

  const labelRender = ({ value, label }: LabelInValueType) => {
    return (
      <Space key={value} direction="vertical" className="tag-container">
        <span>{label}</span>
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}>
          <DatePicker
            allowClear
            placeholder="Выберите дату проведения"
            format={DateTimeFormat.DATE}
            locale={locale}
            value={datesMap[value]}
            disabledDate={(current: DatePickerProps['value']) => {
              if (!current) return false;

              return current < birthDate || current > today;
            }}
            onChange={(date) => handleDatePickerChange(String(value), date)}
          />
        </div>
      </Space>
    );
  };

  const options = events?.map((protocol: EventProtocol) => ({
    value: protocol.id,
    label: `«${
      dictionaries?.dictMap?.RESEARCH_TYPE?.[protocol.researchTypeId]?.name ?? ''
    }»: ${protocol.ageFrom}${protocol.ageTo ? ` - ${protocol.ageTo}` : ''} дн.`,
  }));

  return (
    <Select
      allowClear
      mode="multiple"
      optionFilterProp="label"
      rootClassName="create-individual-events-select"
      placeholder="Выберите мероприятия"
      value={mapValue(value)}
      options={options}
      disabled={disabled}
      onChange={handleChange}
      labelRender={labelRender}
    />
  );
};
